// @djsem/shared — Release Campaign Planner (36), Post-Show Recorder (37),
// Library Cleanup (38), Pro SDK (39), Marketplace (35).
//
// Five modules in one file, each clearly delimited. All follow established
// contract + service patterns. No UI or backend routes — those are dependency-reported.

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "../index";
import { SeqCounter } from "../runtime";
const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 35 — Creator Marketplace Foundation
// ============================================================================

export type DJSEMMarketplaceItemType =
  | "show_pack" | "visual_scene_pack" | "brand_world" | "lighting_preset_pack"
  | "stem_pack" | "transition_pack" | "release_campaign_template"
  | "typography_pack" | "venue_profile" | "adapter_mapping_pack" | "tutorial_pack";

export type DJSEMMarketplaceListingStatus =
  | "draft" | "submitted_for_review" | "approved" | "published" | "rejected" | "suspended" | "archived";

export interface DJSEMMarketplaceRightsSummary {
  allAssetsOwnedOrLicensed: boolean;
  containsAIAssets: boolean;
  containsThirdPartyAudio: boolean;
  containsThirdPartyLyrics: boolean;
  blockedReasons: string[];
  warnings: string[];
}

export interface DJSEMModerationSummary {
  status: "not_checked" | "passed" | "flagged" | "blocked";
  flags: string[];
  reviewedBy?: string;
  reviewedAt?: string;
}

export interface DJSEMMarketplaceLicense {
  id: string;
  label: string;
  usage: "personal" | "commercial_creator" | "venue_use" | "enterprise_custom";
  redistributionAllowed: boolean;
  remixAllowed: boolean;
  attributionRequired: boolean;
  warnings: string[];
}

export interface DJSEMMarketplaceListing {
  id: string;
  sellerOrganisationId: string;
  itemType: DJSEMMarketplaceItemType;
  title: string;
  description: string;
  previewAssetIds: string[];
  sourceAssetIds: string[];
  templateIds: string[];
  priceModel: "free" | "paid_fixed" | "included_with_plan" | "custom_quote";
  priceCents?: number;
  currency?: string;
  licenseId: string;
  status: DJSEMMarketplaceListingStatus;
  rightsSummary: DJSEMMarketplaceRightsSummary;
  moderationSummary: DJSEMModerationSummary;
  createdAt: string;
  updatedAt: string;
}

export interface DJSEMRevenueSharePolicy {
  id: string;
  sellerSharePercent: number;
  platformSharePercent: number;
  paymentProvider: "unconfigured" | "stripe_connect" | "manual";
  status: "draft" | "active" | "disabled";
}

export interface DJSEMMarketplaceInstallResult {
  listingId: string;
  installedResourceIds: string[];
  warnings: string[];
}

export interface CreateListingInput extends Omit<DJSEMMarketplaceListing, "id" | "status" | "createdAt" | "updatedAt"> {
  id?: string;
}

export class DJSEMMarketplaceListingService {
  createDraftListing(input: CreateListingInput): DJSEMMarketplaceListing {
    return { ...input, id: input.id ?? `mpl_${_seq.next()}`, status: "draft", createdAt: AT, updatedAt: AT };
  }

  validateListing(listing: DJSEMMarketplaceListing): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!listing.title.trim()) issues.push({ path: "$.title", message: "required" });
    if (!listing.licenseId) issues.push({ path: "$.licenseId", message: "required" });
    if (listing.previewAssetIds.length === 0) issues.push({ path: "$.previewAssetIds", message: "at least one preview required" });
    if (!listing.rightsSummary.allAssetsOwnedOrLicensed) issues.push({ path: "$.rightsSummary", message: "all assets must be owned or licensed" });
    if (listing.rightsSummary.blockedReasons.length > 0) issues.push({ path: "$.rightsSummary.blockedReasons", message: listing.rightsSummary.blockedReasons.join("; ") });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "marketplace_listing");
  }

  submitForReview(listing: DJSEMMarketplaceListing): DJSEMMarketplaceListing {
    const v = this.validateListing(listing);
    if (!v.ok) return listing; // unchanged — blocked
    return { ...listing, status: "submitted_for_review", updatedAt: AT };
  }

  approveListing(listing: DJSEMMarketplaceListing): DJSEMMarketplaceListing {
    if (listing.status !== "submitted_for_review") return listing;
    return { ...listing, status: "approved", updatedAt: AT };
  }

  rejectListing(listing: DJSEMMarketplaceListing, reason: string): DJSEMMarketplaceListing {
    return { ...listing, status: "rejected", updatedAt: AT };
  }

  publishListing(listing: DJSEMMarketplaceListing): DJSEMMarketplaceListing {
    if (listing.status !== "approved") return listing;
    return { ...listing, status: "published", updatedAt: AT };
  }

  installListing(listing: DJSEMMarketplaceListing): DJSEMMarketplaceInstallResult {
    // Install creates project-local copies — never mutates source
    const resourceIds = listing.templateIds.map(tid => `inst_${_seq.next()}_${tid}`);
    return { listingId: listing.id, installedResourceIds: resourceIds, warnings: [] };
  }
}
